import { el, clear } from "./render";
import qrcode from "qrcode-generator";
import { generateSecretKey, getPublicKey, nip19 } from "nostr-tools";
import type { Session } from "../session";
import { signInWithKey, signInWithBunker, signInWithNostrConnect } from "../session";

/**
 * First run.
 *
 * The cryptography is treated as ordinary account setup with unusually honest
 * copy — no lock icons, no shields. A client that advertises its encryption
 * looks like it is compensating.
 *
 * Two doors, and the phone one is listed first because it is the better idea:
 * the key never reaches the browser at all.
 */

/**
 * Relays the signer and this browser use to find each other.
 *
 * Public on purpose: NIP-46 traffic is encrypted end to end, so these see only
 * ciphertext, and the mail relay is members-only and could not carry the
 * throwaway key this handshake uses.
 *
 * More than one because the handshake dies silently if the single relay is
 * unreachable — relay.nsec.app, the conventional choice, was unreachable when
 * this was written.
 */
const CONNECT_RELAYS = (import.meta.env["VITE_NIP46_RELAYS"] ??
  "wss://relay.damus.io,wss://nos.lol")
  .split(",")
  .map((r: string) => r.trim())
  .filter(Boolean);

export function renderSignIn(
  root: HTMLElement,
  picker: HTMLSelectElement,
  onSession: (session: Session) => void,
  restoreFailure?: string,
): void {
  clear(root);

  // Says why a stored session was dropped, so a failed reconnect is not silent.
  const dropped = el(
    "p",
    { class: "fineprint restore-failure", role: "status" },
    `Stored session could not be restored: ${restoreFailure ?? ""}`,
  );
  dropped.hidden = !restoreFailure;

  const error = el("p", { class: "error", role: "alert" });
  error.hidden = true;
  const note = el("p", { class: "note" });
  note.hidden = true;

  const fail = (e: unknown) => {
    error.textContent = e instanceof Error ? e.message : String(e);
    error.hidden = false;
  };

  // ---- phone ----
  const qrBox = el("div", { class: "qr" });
  const uriField = el("input", { class: "uri", readonly: "true", "aria-label": "Connection link" });
  const status = el("p", { class: "fineprint" });
  const phonePanel = el("div", { class: "panel" }, qrBox, uriField, note, status);
  phonePanel.hidden = true;

  let abort: AbortController | null = null;

  const startPhone = () => {
    abort?.abort();
    abort = new AbortController();
    phonePanel.hidden = false;
    error.hidden = true;

    const clientSecret = generateSecretKey();
    const clientPub = getPublicKey(clientSecret);
    const secretToken = Math.random().toString(36).slice(2, 10);
    const relayParams = CONNECT_RELAYS.map(
      (r: string) => `relay=${encodeURIComponent(r)}`,
    ).join("&");
    const uri =
      `nostrconnect://${clientPub}` +
      `?${relayParams}` +
      `&secret=${secretToken}` +
      `&perms=${encodeURIComponent("sign_event,nip44_encrypt,nip44_decrypt")}` +
      `&name=${encodeURIComponent("nicemail.ca")}`;

    uriField.value = uri;
    drawQr(qrBox, uri);
    note.textContent = "Scan with Amber, then approve. Waiting…";
    note.hidden = false;

    void signInWithNostrConnect(uri, clientSecret, abort.signal)
      .then(onSession)
      .catch((e: unknown) => {
        if (abort?.signal.aborted) return;
        // The handshake fails quietly when a relay is unreachable, which looks
        // like nothing happening; say which relays were tried.
        fail(
          new Error(
            `${e instanceof Error ? e.message : String(e)} — tried ${CONNECT_RELAYS.join(", ")}`,
          ),
        );
        note.hidden = true;
      });
  };

  // ---- bunker paste ----
  const bunkerInput = el("input", {
    class: "uri",
    type: "text",
    placeholder: "bunker://…",
    "aria-label": "Bunker connection string",
    spellcheck: "false",
  });
  const bunkerButton = el("button", { class: "secondary", type: "button" }, "Connect");
  bunkerButton.addEventListener("click", () => {
    error.hidden = true;
    bunkerButton.disabled = true;
    void signInWithBunker(bunkerInput.value, (url) => window.open(url, "_blank", "noopener"))
      .then(onSession)
      .catch((e: unknown) => {
        fail(e);
        bunkerButton.disabled = false;
      });
  });

  // ---- local key ----
  const keyInput = el("input", {
    id: "key",
    type: "password",
    autocomplete: "off",
    spellcheck: "false",
    placeholder: "nsec1…",
  });
  const keyButton = el("button", { class: "primary", type: "submit" }, "Open Mail");
  const stay = el("input", { id: "stay", type: "checkbox" });
  const keyForm = el(
    "form",
    { class: "panel" },
    el("label", { for: "key" }, "Private key"),
    keyInput,
    keyButton,
    el(
      "label",
      { class: "checkline", for: "stay" },
      stay,
      el("span", {}, "Keep this key in the browser"),
    ),
    el(
      "p",
      { class: "fineprint" },
      "Off, the key is held in this tab only and a refresh signs you out. On, it is " +
        "stored on this device \u2014 your mail is then only as private as this browser profile.",
    ),
  );
  keyForm.hidden = true;
  keyForm.addEventListener("submit", (event) => {
    event.preventDefault();
    error.hidden = true;
    keyButton.disabled = true;
    void signInWithKey(keyInput.value, stay.checked)
      .then(onSession)
      .catch((e: unknown) => {
        fail(e);
        keyButton.disabled = false;
      });
  });

  // ---- tabs ----
  const tabPhone = el("button", { class: "tab", type: "button", "aria-current": "true" }, "Approve on Phone");
  const tabKey = el("button", { class: "tab", type: "button", "aria-current": "false" }, "Paste a Key");
  const choose = (phone: boolean) => {
    tabPhone.setAttribute("aria-current", String(phone));
    tabKey.setAttribute("aria-current", String(!phone));
    phonePanel.hidden = !phone;
    bunkerRow.hidden = !phone;
    keyForm.hidden = phone;
    if (phone) startPhone();
    else abort?.abort();
  };
  tabPhone.addEventListener("click", () => choose(true));
  tabKey.addEventListener("click", () => choose(false));

  const bunkerRow = el(
    "div",
    { class: "bunker" },
    el("span", { class: "fineprint" }, "Or paste a connection string from your signer:"),
    el("div", { class: "row-inline" }, bunkerInput, bunkerButton),
  );

  const mark = el(
    "div",
    { class: "mark" },
    el("span", { class: "dots" }, el("i", {}), el("i", {}), el("i", {})),
    el("span", {}, "built by punk.science"),
  );
  mark.append(picker);

  root.append(
    el(
      "div",
      { class: "app signin-app" },
      el(
        "div",
        { class: "signin" },
        el("h1", {}, "nicemail.ca"),
        el("p", {}, "Your key opens your mail. Nothing else can."),
        dropped,
        el("div", { class: "tabs" }, tabPhone, tabKey),
        phonePanel,
        bunkerRow,
        keyForm,
        error,
      ),
      mark,
    ),
  );

  choose(true);
}

/** Render a QR as an inline SVG, so it inherits the theme's ink colour. */
function drawQr(host: HTMLElement, text: string): void {
  clear(host);
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  const count = qr.getModuleCount();
  const parts: string[] = [];
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) parts.push(`M${c} ${r}h1v1h-1z`);
    }
  }
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `-1 -1 ${count + 2} ${count + 2}`);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "Connection code for a Nostr signer");
  const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  bg.setAttribute("x", "-1");
  bg.setAttribute("y", "-1");
  bg.setAttribute("width", String(count + 2));
  bg.setAttribute("height", String(count + 2));
  bg.setAttribute("fill", "var(--surface)");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", parts.join(""));
  path.setAttribute("fill", "var(--ink)");
  svg.append(bg, path);
  host.append(svg);
}

export { nip19 };
